/**
 * Derived registries + wire message unions for UserDo entities.
 *
 * Everything here is **auto-derived** from `entitySchema` filtered by
 * `authority` — adding or removing a UserDo entity in
 * `schema/entities.ts` automatically updates these maps and types.
 * Server dispatch (`server/userdo.ts`) and client dispatch
 * (`src/sync/internal/...`) consume these.
 *
 * Edit only when changing the framework's dispatch model. To add a new
 * entity, edit `schema/entities.ts`, not this file.
 *
 * Phase A note: the unified `entitySchema` now carries both UserDo and
 * StudyGroupDo entities; the `authority` filter below restricts this
 * view to UserDo's slice. The mirror filter lives in
 * `group-entity-registry.ts`.
 */

import type { z } from 'zod';

import { type EntitySchema, entitySchema } from '../schema/entities';
import type { SyncEntity } from './define-sync-entity';

// ---------------------------------------------------------------------------
// Wire message unions — derived from the phantom `PushMessage` /
// `BroadcastMessage` / `DeleteMessage` carriers each `defineEntity` result
// exposes. `never` from entities without delete types collapses out
// automatically.
//
// Phase A: these unions are SUPERSETS of UserDo's true wire surface
// (they include group-side message types too) because the phantom
// carriers don't yet thread `authority` through at the type level.
// Discrimination by `type` at runtime keeps this safe — UserDo's
// dispatch maps below only carry the keys for user-authority entities,
// so an `sg-*-push` message arriving on a UserDo socket simply finds
// no handler. Tightening the type-level filter is a Phase C item.
// ---------------------------------------------------------------------------

export type EntityPushMessage = {
  [K in keyof EntitySchema]: EntitySchema[K]['PushMessage'];
}[keyof EntitySchema];

export type EntityBroadcastMessage = {
  [K in keyof EntitySchema]: EntitySchema[K]['BroadcastMessage'];
}[keyof EntitySchema];

export type EntityDeleteMessage = {
  [K in keyof EntitySchema]: EntitySchema[K]['DeleteMessage'];
}[keyof EntitySchema];

export type EntityDeleteBroadcastMessage = {
  [K in keyof EntitySchema]: EntitySchema[K]['DeleteBroadcastMessage'];
}[keyof EntitySchema];

export type EntityBulkPushMessage = {
  [K in keyof EntitySchema]: EntitySchema[K]['BulkPushMessage'];
}[keyof EntitySchema];

export type EntityBulkBroadcastMessage = {
  [K in keyof EntitySchema]: EntitySchema[K]['BulkBroadcastMessage'];
}[keyof EntitySchema];

export type EntityBulkDeleteMessage = {
  [K in keyof EntitySchema]: EntitySchema[K]['BulkDeleteMessage'];
}[keyof EntitySchema];

export type EntityBulkDeleteBroadcastMessage = {
  [K in keyof EntitySchema]: EntitySchema[K]['BulkDeleteBroadcastMessage'];
}[keyof EntitySchema];

// ---------------------------------------------------------------------------
// Runtime registries — UserDo-only view of the unified schema.
//
// An entity participates if its `authority` is `'user'` or `'both'`.
// `'group'`-only entities are excluded; they're consumed by the
// StudyGroupDo registry in `group-entity-registry.ts`.
// ---------------------------------------------------------------------------

const allEntities: SyncEntity<unknown>[] = Object.values(entitySchema)
  .map((s) => s.entity as SyncEntity<unknown>)
  .filter((e) => e.authority === 'user' || e.authority === 'both');

/** All entities that follow the generic defineSyncEntity pattern (non-record). */
export const genericEntities: SyncEntity<unknown>[] = allEntities.filter(
  (e) => e.type !== 'record',
);

/** Map from push message type → entity (for server dispatch in userdo.ts). */
export const pushHandlerMap: Record<string, SyncEntity<unknown>> = Object.fromEntries(
  genericEntities.map((e) => [e.pushType, e]),
);

/** Map from broadcast message type → entity (for client dispatch). */
export const broadcastHandlerMap: Record<
  string,
  SyncEntity<unknown>
> = Object.fromEntries(genericEntities.map((e) => [e.broadcastType, e]));

/** All record entities (per-entry DO keys with independent timestamps). */
export const recordEntities: SyncEntity<unknown>[] = allEntities.filter(
  (e) => e.type === 'record',
);

/** Map from record push message type → entity (for server dispatch). */
export const recordPushHandlerMap: Record<
  string,
  SyncEntity<unknown>
> = Object.fromEntries(recordEntities.map((e) => [e.pushType, e]));

/** Map from record delete message type → entity (for server dispatch). */
export const recordDeleteHandlerMap: Record<
  string,
  SyncEntity<unknown>
> = Object.fromEntries(
  recordEntities.filter((e) => e.deleteType).map((e) => [e.deleteType!, e]),
);

/** Map from bulk push type → record entity (server dispatches to
 *  `handleBulkPush`). Auto-derived alongside the per-entry map so adding
 *  a record entity to `entitySchema` gets bulk dispatch for free. */
export const recordBulkPushHandlerMap: Record<
  string,
  SyncEntity<unknown>
> = Object.fromEntries(
  recordEntities.filter((e) => e.bulkPushType).map((e) => [e.bulkPushType!, e]),
);

/** Map from bulk delete type → record entity. */
export const recordBulkDeleteHandlerMap: Record<
  string,
  SyncEntity<unknown>
> = Object.fromEntries(
  recordEntities.filter((e) => e.bulkDeleteType).map((e) => [e.bulkDeleteType!, e]),
);

/**
 * Map from inbound message type → Zod validator for the full message
 * envelope. The server calls `validator.safeParse(msg)` before dispatch
 * and rejects malformed payloads with `sync-error`. Built from each
 * entity's declared Zod schema — no per-entity wiring required.
 */
export const inboundValidators: Record<string, z.ZodTypeAny> = (() => {
  const map: Record<string, z.ZodTypeAny> = {};
  for (const e of allEntities) {
    if (e.pushMessageValidator) map[e.pushType] = e.pushMessageValidator;
    if (e.deleteType && e.deleteMessageValidator) {
      map[e.deleteType] = e.deleteMessageValidator;
    }
    if (e.bulkPushType && e.bulkPushMessageValidator) {
      map[e.bulkPushType] = e.bulkPushMessageValidator;
    }
    if (e.bulkDeleteType && e.bulkDeleteMessageValidator) {
      map[e.bulkDeleteType] = e.bulkDeleteMessageValidator;
    }
  }
  return map;
})();
